import {Meeting} from 'event/models/Meeting'
import {UpdateMeetingData} from 'organization/Event/Management/UpdateMeetingData'
import {organizationApiClient} from 'organization/organizationApiClient'
import {FullPageLoader} from 'organization/theme/FullPageLoader'
import React, {useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {useRouteOrganizationEventId} from 'utils/lib/url'

export interface MeetingContextProps {
  meeting: Meeting | null
  updateMeeting: (
    data: UpdateMeetingData,
  ) => Promise<string | boolean | undefined>
  getStartLink: () => Promise<string>
  getStreamURL: () => Promise<{streamUrl: string; streamKey: string} | false>
  loading: boolean
}

export const MeetingContext = React.createContext<
  MeetingContextProps | undefined
>(undefined)

export default function MeetingProvider(props: {children: React.ReactElement}) {
  const apiClient = organizationApiClient()
  const [meeting, setMeeting] = useState<Meeting | null>(null)
  const [initLoading, setInitLoading] = useState<boolean>(true)
  const [loading, setLoading] = useState<boolean>(true)
  const eventId = useRouteOrganizationEventId()

  useEffect(() => {
    setInitLoading(true)
    get()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const get = async () => {
    setLoading(true)
    await apiClient
      .get(`api/event/${eventId}/meeting`)
      .then((res) => setMeeting(res))
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })
      .finally(() => {
        setLoading(false)
        setInitLoading(false)
      })
  }

  const updateMeeting = async (data: UpdateMeetingData) => {
    if (loading) return
    setLoading(true)
    const result = await apiClient
      .post(`api/event/${eventId}/meeting`, data)
      .then(get)
      .then(() => {
        return true
      })
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })
      .finally(() => setLoading(false))

    if (result) return true

    return false
  }

  const getStartLink = async () => {
    if (loading) return
    setLoading(true)
    const result = await apiClient
      .get(`api/event/${eventId}/meeting/start-link`)
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })
      .finally(() => setLoading(false))

    return result
  }

  const getStreamURL = async () => {
    if (loading) return
    setLoading(true)
    const result = await apiClient
      .get(`api/event/${eventId}/meeting/stream-url`)
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })
      .finally(() => setLoading(false))

    return result
  }

  return (
    <MeetingContext.Provider
      value={{
        meeting,
        updateMeeting,
        getStartLink,
        getStreamURL,
        loading,
      }}
    >
      {initLoading && <FullPageLoader />}
      {!initLoading && props.children}
    </MeetingContext.Provider>
  )
}

export function useMeeting() {
  const context = React.useContext(MeetingContext)
  if (context === undefined) {
    throw new Error('useMeeting must be used within MeetingProvider')
  }
  return context
}
